import { AfterViewInit, Component, EventEmitter, Injectable, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LanguageSupportService } from './services/language-support.service';
import { LocalSettingsService } from './services/local-settings/local-settings.service';
import { EventTypes, OidcSecurityService, PublicEventsService } from 'angular-auth-oidc-client';
import { MsalBroadcastService } from '@azure/msal-angular';
import { EventMessage, EventType } from '@azure/msal-browser';
import { isNotBlank, isNotNullAndUndefined } from './utilities/utils';
import { AuthCookieManagerService } from './services/user-auth/auth-cookie-manager/auth-cookie-manager.service';
import { authCookieKey } from './utilities/constant';
import { UserAuthService } from './services/user-auth/user-auth.service';
import { TokenRefreshService } from './services/user-auth/token-refresh.service';
import { LoaderService } from './services/loader/loader.service';
import { filter } from 'rxjs/operators';
import { NavController, Platform } from '@ionic/angular';
import { NotificationServiceService } from './services/notification/notification-service.service';
import { ShareValuesService } from './services/sharedValues/share-values.service';
import { RefreshAlertService } from './services/refresh-alert.service';
import { NavigationEnd, Router } from '@angular/router';
import { NgSelectConfig } from '@ng-select/ng-select';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit {
  showLoader = false;
  selectedLanguage: string;
  public showSideMenu: boolean;
  public showHazardOverlay: boolean;

  public get aiFeaturesEnabled(): boolean {
    return this.userAuthService.hasPermission('CHATBOT');
  }

  constructor(private translate: TranslateService,
    public language: LanguageSupportService,
    public localSettingsSrv: LocalSettingsService,
    public notficationServ: NotificationServiceService,
    private loaderService: LoaderService,
    private oidcSecurityService: OidcSecurityService,
    public publicEventsService: PublicEventsService,
    private msalBroadcastService: MsalBroadcastService,
    public authCookieManagerService: AuthCookieManagerService,
    public userAuthService: UserAuthService,
    private tokenRefreshService: TokenRefreshService,
    private nav: NavController,
    private platform: Platform,
    private refreshAlertService: RefreshAlertService,
    public sharedValue: ShareValuesService,
    private router: Router,
    private ngSelectConfig: NgSelectConfig
  ) {
    window.addEventListener('beforeunload', this.showAlertOnRefresh);
    const activeLanguage = this.language.getDefaultLanguage();
    translate.setDefaultLang(activeLanguage);
    translate.use(activeLanguage);
    this.setNgSelectTranslations();
    this.translate.onLangChange.subscribe(() => this.setNgSelectTranslations());
    
    // Initialize loader subscription
    this.loaderService.getLoaderStats().subscribe((res: boolean) => {
       this.showLoader = res;
    });
    
    // Initialize token refresh service
    this.userAuthService.setTokenRefreshService(this.tokenRefreshService);

    // React to MSAL token acquisition failures (e.g. expired/missing refresh
    // token). Without this the failing token request just hangs behind the
    // MsalInterceptor, leaving the user stuck on a blank protected page.
    this.handleMsalAuthFailures();
  }

  /**
   * When MSAL cannot silently obtain a valid token, log the user out cleanly and
   * send them back to the login screen instead of hanging on a blank page.
   */
  private handleMsalAuthFailures(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) =>
          msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE ||
          msg.eventType === EventType.SSO_SILENT_FAILURE
        )
      )
      .subscribe((msg: EventMessage) => {
        console.warn('MSAL token acquisition failed - logging out:', msg.eventType, msg.error);
        this.userAuthService.logOut();
      });
  }

  showAlertOnRefresh = (event: BeforeUnloadEvent) => {
    if (this.refreshAlertService.shouldShowAlert()) {
      event.preventDefault();
      event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.showAlertOnRefresh);
  }

  ngOnInit() {
    this.platform.ready().then(() => {
      //this.initializeAuth();
      // this.checkAuth();
    });
  }

  private setNgSelectTranslations(): void {
    this.ngSelectConfig.notFoundText = this.translate.instant('No Item Found');
  }

  ngAfterViewInit(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const activePage = event.url;
        if (activePage == '/login' || activePage == '/') {
          this.showSideMenu = false;
        } else {
          this.showSideMenu = true;
        }
        if(activePage.includes('clone-roadmap/readonly-roadmap') || activePage.includes('clone-roadmap')) {
          this.showHazardOverlay = true;
        }else {
          this.showHazardOverlay = false;
        }
      });
  }


  checkAuth() {
    if (this.userAuthService.checkMsalAuth() && this.userAuthService.checkUserExisting()) {this.nav.navigateRoot('/dashboard');}
    else {this.nav.navigateRoot('/login');}
  }

  initializeAuth() {
    // publicEvent will be called when refrersh token or silent token will be refreshed automatically
    this.publicEventsService
      .registerForEvents()
      .pipe(filter((notification) => notification.type === EventTypes.SilentRenewStarted))
      .subscribe((value) => {
        const authInfo = this.userAuthService.getAuthenticationInfo();
        authInfo && this.authCookieManagerService.write(authCookieKey, authInfo, true);
      });

    //checking user authentcation
    this.oidcSecurityService.checkAuth().subscribe(({ isAuthenticated, userData, accessToken, idToken }) => {
      if (isAuthenticated && isNotBlank(accessToken) && isNotNullAndUndefined(accessToken)) {
        const authInfo = this.userAuthService.getAuthenticationInfo();
        authInfo && this.authCookieManagerService.write(authCookieKey, authInfo);
        this.nav.navigateRoot('/dashboard');
      } else {
        const cookieInfo = this.authCookieManagerService.read(authCookieKey);
        if (cookieInfo && isNotBlank(cookieInfo.cookie_expiry_time) && isNotNullAndUndefined(cookieInfo.cookie_expiry_time) &&
          isNotBlank(cookieInfo.access_token) && isNotNullAndUndefined(cookieInfo.access_token)) {this.userAuthService.login();}
        else {this.nav.navigateRoot('/login');}
      }
    },err=>{
    });
  }
}
